import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { validateEmail } from '@/utils/String.ts'
import { useToast } from '@ai/ui-components/toast'
import { useUserRoleOptions, VALID_ROLES } from '@/composables/users/useUserRoleOptions'
import { useInviteUsersBulkMutation, type BulkInviteResult } from '@/composables/queries'

interface UseUserInviteFormOptions {
  users: Ref<Record<string, string>[]>
  open: Ref<boolean>
  hasUnsavedChanges: Ref<boolean>
  onUserInvited: () => void
  onReset: () => void
}

export const useUserInviteForm = ({
  users,
  open,
  hasUnsavedChanges,
  onUserInvited,
  onReset,
}: UseUserInviteFormOptions) => {
  const { t, locale: i18nLocale } = useI18n()
  const toast = useToast()
  const { options: localizedRoleOptions } = useUserRoleOptions()

  const showCancelConfirmation = ref(false)
  const submittedRowIds = ref(new Set<string>())
  const touchedFields = ref(new Set<string>())

  const rowIds = new WeakMap<Record<string, string>, string>()
  let nextRowId = 0
  const getRowId = (row: Record<string, string>): string => {
    const existing = rowIds.get(row)
    if (existing !== undefined) return existing
    const id = String(nextRowId++)
    rowIds.set(row, id)
    return id
  }
  const duplicateEmails = computed(() => {
    const counts = new Map<string, number>()
    for (const u of users.value) {
      const key = u.email.toLowerCase()
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    const duplicates = new Set<string>()
    for (const [email, count] of counts) {
      if (count > 1) duplicates.add(email)
    }
    return duplicates
  })

  const rowErrors = computed(() =>
    users.value.map((u) => {
      const id = getRowId(u)
      const rowSubmitted = submittedRowIds.value.has(id)
      const emailTouched = touchedFields.value.has(`${id}:email`)
      const roleTouched = touchedFields.value.has(`${id}:role`)

      const showEmail = rowSubmitted || emailTouched
      const showRole = rowSubmitted || roleTouched

      let emailError = ''
      if (showEmail) {
        if (!u.email) emailError = t('users_import.email_required')
        else if (duplicateEmails.value.has(u.email.toLowerCase()))
          emailError = t('users_import.duplicate_email')
        else if (!validateEmail(u.email)) emailError = t('please_enter_valid_email')
      }

      const roleError = showRole && !u.role ? t('users_import.role_required') : ''

      return { email: emailError, role: roleError }
    }),
  )

  const hasValidUsers = computed(
    () =>
      users.value.length > 0 &&
      duplicateEmails.value.size === 0 &&
      users.value.every((u) => validateEmail(u.email) && VALID_ROLES.includes(u.role)),
  )

  const { mutateAsync, isPending: isSubmitting } = useInviteUsersBulkMutation()

  const panelOpen = computed({
    get: () => open.value,
    set: (newValue: boolean | undefined) => {
      if (!newValue && open.value && hasUnsavedChanges.value) {
        showCancelConfirmation.value = true
        return
      }
      open.value = newValue ?? false
      if (!newValue) doReset()
    },
  })

  const handleCancel = (): boolean => {
    if (hasUnsavedChanges.value) {
      showCancelConfirmation.value = true
      return false
    }
    open.value = false
    doReset()
    return true
  }

  const confirmDiscard = (): boolean => {
    showCancelConfirmation.value = false
    open.value = false
    doReset()
    return true
  }

  const keepEditing = (): boolean => {
    showCancelConfirmation.value = false
    return true
  }

  const doReset = (): void => {
    submittedRowIds.value = new Set()
    touchedFields.value = new Set()
    onReset()
  }

  const showInviteToast = (
    invited: number,
    reinvited: number,
    alreadyMembers: number,
    otherErrors: number,
  ): void => {
    if (invited > 0) toast.success(t('users_import.success', { count: invited }))
    if (reinvited > 0) toast.info(t('invitation_resent_successfully', { count: reinvited }))
    if (alreadyMembers > 0)
      toast.error(t('users_import.already_members', { count: alreadyMembers }), {
        title: t('error_occurred'),
      })
    if (otherErrors > 0)
      toast.error(t('users_import.partial_error', { count: otherErrors }), {
        title: t('error_occurred'),
      })
  }

  const handleFieldBlur = (index: number, field: string): void => {
    const row = users.value[index]
    touchedFields.value.add(`${getRowId(row)}:${field}`)
  }

  const markAllSubmitted = (): void => {
    submittedRowIds.value = new Set(users.value.map(getRowId))
  }

  const handleSubmit = async (): Promise<boolean> => {
    if (isSubmitting.value) return false
    if (!hasValidUsers.value) {
      markAllSubmitted()
      return false
    }

    const uniqueUsers = users.value
      .map(({ email, role }) => ({ email, role }))
      .filter((u, i, arr) => arr.findIndex((x) => x.email === u.email) === i)

    let results: BulkInviteResult[]
    try {
      const response = await mutateAsync({ users: uniqueUsers, locale: i18nLocale.value })
      results = response.results
    } catch {
      toast.error(t('error_occurred'))
      return false
    }

    const count = (pred: (r: BulkInviteResult) => boolean) => results.filter(pred).length
    const invited = count((r) => r.status === 'invited')
    const reinvited = count((r) => r.status === 'reinvited')
    const alreadyMembers = count((r) => r.status === 'error' && r.error === 'already_exists')
    const otherErrors = count((r) => r.status === 'error' && r.error !== 'already_exists')

    showInviteToast(invited, reinvited, alreadyMembers, otherErrors)

    if (invited > 0 || reinvited > 0) {
      onUserInvited()
      open.value = false
      doReset()
    }
    return true
  }

  return {
    localizedRoleOptions,
    showCancelConfirmation,
    rowErrors,
    hasValidUsers,
    isSubmitting,
    panelOpen,
    handleCancel,
    confirmDiscard,
    keepEditing,
    handleFieldBlur,
    handleSubmit,
    markAllSubmitted,
  }
}
